import { PageTitles } from '@revelio/core';

/** Body copy under the opening "Welcome to …" headline on the tour's intro card. */
export const INTRO_LEDE =
  'Understand the workforce by tracking companies, analyzing markets, and discovering talent, all in one place.';

/**
 * Control-panel sections whose copy is fixed. Keyed by the nav section's
 * `name` (the `PageTitles` value rendered in the rail). Applications and Data
 * are absent here — their copy depends on which child links the user has, so
 * they're resolved separately (see {@link resolveNavSectionCopy}).
 *
 * A section only gets a step when it renders for the user, so a missing key —
 * or a section the user's permissions filtered out of the rail — simply never
 * appears in the tour.
 */
const STATIC_SECTION_COPY: Readonly<Record<string, string>> = {
  [PageTitles.DASHBOARD_NAV]:
    'Track the workforce statistics of any company, geography or role. See things like headcount, hiring & attrition trends, pay, postings, and sentiment.',
  [PageTitles.DISCOVERY_NAV]:
    'Search across people, companies, geographies, roles, and postings to identify leads and shifts in the market.',
  [PageTitles.REPORTS_NAV]:
    'Generate in-depth reports on a company or market, tracking compensation, AI exposure, attrition and more.',
};

/**
 * The Data step describes Data Builder, so it needs that link: the section
 * also renders for a user whose only Data link is the Data Dictionary.
 */
function resolveDataCopy(childNames: readonly string[]): string | null {
  if (!childNames.includes(PageTitles.DATA_BUILDER)) return null;
  return 'Pull the underlying datasets, including profiles, postings, transitions, sentiment and more. All datasets can be delivered by downloadable links, S3 or Snowflake.';
}

/**
 * Applications is the one section whose step adapts to the user. It always
 * renders in the rail (its Economic Impact link isn't tab-gated), but Economic
 * Impact isn't a shipped app, so the step is driven entirely by the two
 * enrichment tools — and a user with neither gets no Applications step at all.
 */
function resolveApplicationsCopy(childNames: readonly string[]): string | null {
  const hasDataEnrichment = childNames.includes(PageTitles.DATA_ENRICHMENT);
  const hasResumeEnrichment = childNames.includes(PageTitles.RESUME_ENRICHMENT);

  if (hasDataEnrichment && hasResumeEnrichment) {
    return 'Enrich internal data, parse résumés, run impact reports, and more within our library of workforce tools.';
  }
  if (hasDataEnrichment) {
    return 'Enrich your own list of companies or people with our workforce data.';
  }
  if (hasResumeEnrichment) {
    return 'Parse your own résumés into structured, enriched profiles.';
  }
  return null;
}

/**
 * Copy for a control-panel section, given the child links the rail rendered
 * under it. Returns `null` when the section should have no tour step — an
 * unmapped section, an Applications section with no enrichment tools, or a
 * Data section without Data Builder.
 */
export function resolveNavSectionCopy(
  sectionName: string,
  childNames: readonly string[]
): string | null {
  if (sectionName === PageTitles.APPLICATIONS_NAV) {
    return resolveApplicationsCopy(childNames);
  }
  if (sectionName === PageTitles.DELIVERABLES) {
    return resolveDataCopy(childNames);
  }
  return STATIC_SECTION_COPY[sectionName] ?? null;
}
